import { Inject, Injectable, Optional } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import * as moment from 'moment';
import { DOCUMENT } from '@angular/common';

export interface Event {
  id: string | number;
  name: string;
  description?: string | null;
  place?: string;
  startAt: Date;
  endAt: Date;
  cover?: any[];
  coverUri?: string;
}

/**
 * Servicio para gestionar metadatos SEO, incluyendo Open Graph y Twitter Cards
 * Optimizado para funcionar tanto en el cliente como en el servidor (SSR)
 */
@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private isServer: boolean = false;
  private origin: string = '';

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST) private request: any
  ) {
    // Detectar si estamos en el servidor o en el cliente
    this.isServer = !!this.request;

    // Obtener el origen para URLs absolutas
    if (this.isServer && this.request) {
      this.origin = `${this.request.protocol}://${this.request.get('host')}`;
    } else if (typeof window !== 'undefined') {
      this.origin = window.location.origin;
    }
  }

  /**
   * Establece los meta tags para un evento, utilizando tanto el API de Meta angular
   * como manipulación directa del DOM para SSR con curl/Postman
   */
  setEventMetaTags(event: Event, url: string): void {
    if (!event) {
      console.warn('SeoService: No hay evento para establecer meta tags');
      return;
    }

    try {
      // Log extenso para depuración
      console.log('\n========= SeoService: Estableciendo meta tags para evento =========');
      console.log(`Evento: ${event.name} (${event.id})`);
      console.log(`URL: ${url}`);
      console.log(`Ejecución en: ${this.isServer ? 'SERVIDOR' : 'CLIENTE'}`);
      if (this.isServer) {
        console.log(`User-Agent: ${this.request?.headers?.['user-agent'] || 'No disponible'}`);
      }

      // Establecer título de la página
      const eventTitle = `${event.name} | TIX`;
      this.title.setTitle(eventTitle);
      console.log(`Título establecido: ${eventTitle}`);

      // Formatear fecha y hora para mostrar
      const startDate = moment(event.startAt).format('DD/MM/YYYY');
      const startTime = moment(event.startAt).format('h:mm A');
      const eventDateFormatted = `${startDate} ${startTime}`;

      // Obtener URL de imagen o usar fallback
      let imageUrl = 'https://tix-shared.s3.us-east-1.amazonaws.com/tix-default-bg.jpeg';
      if (event.cover && event.cover.length > 0 && event.cover[0]?.attributes?.url) {
        imageUrl = event.cover[0].attributes.url;
      } else if (event.coverUri) {
        imageUrl = event.coverUri;
      }
      console.log(`Imagen: ${imageUrl}`);

      // Crear descripción con detalles del evento
      const description = `${event.name} - ${startDate} a las ${startTime}${
        event.place ? ` en ${event.place}` : ''
      }. ${event.description?.substring(0, 150) || ''}`.trim();

      // Garantizar URLs absolutas para crawlers
      const absoluteUrl = this.getAbsoluteUrl(url);
      const absoluteImageUrl = this.getAbsoluteUrl(imageUrl);

      console.log(`URL absoluta: ${absoluteUrl}`);
      console.log(`URL imagen absoluta: ${absoluteImageUrl}`);

      // Eliminar meta tags existentes para evitar duplicados
      this.removeExistingMetaTags();

      // Definir estructura de meta tag
      interface MetaTag {
        name?: string;
        property?: string;
        content: string;
      }

      // Definir todos los meta tags que vamos a establecer
      const metaTags: MetaTag[] = [
        // Open Graph tags - fundamentales para compartir en redes sociales
        { property: 'og:title', content: `${event.name} - ${eventDateFormatted}` },
        { property: 'og:type', content: 'event' },
        { property: 'og:url', content: absoluteUrl },
        { property: 'og:image', content: absoluteImageUrl },
        { property: 'og:description', content: description },
        { property: 'og:site_name', content: 'TIX' },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:locale', content: 'es_ES' },
        { property: 'og:image:alt', content: event.name },

        // Twitter Card tags - necesarios para vista previa en Twitter
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: `${event.name} - ${eventDateFormatted}` },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: absoluteImageUrl },
        { name: 'twitter:image:alt', content: event.name },

        // Meta tags estándar para SEO
        { name: 'description', content: description }
      ];

      // Agregar tags específicos del evento si están disponibles
      if (event.startAt) {
        metaTags.push({
          property: 'og:event:start_time',
          content: new Date(event.startAt).toISOString(),
        });
      }

      if (event.endAt) {
        metaTags.push({
          property: 'og:event:end_time',
          content: new Date(event.endAt).toISOString(),
        });
      }

      if (event.place) {
        metaTags.push({
          property: 'og:event:location',
          content: event.place,
        });
      }

      // En el servidor, insertar meta tags directamente en el DOM
      if (this.isServer && this.document) {
        this.insertMetaTagsDirectly(metaTags);
      }

      // Actualizar cada meta tag a través del API de Angular
      metaTags.forEach((tag: MetaTag) => {
        try {
          const metaDefinition: any = { content: tag.content || '' };

          if (tag.property) {
            metaDefinition.property = tag.property;
          }

          if (tag.name) {
            metaDefinition.name = tag.name;
          }

          this.meta.updateTag(metaDefinition);
        } catch (error) {
          console.error(`Error al actualizar meta tag: ${tag.property || tag.name}`, error);
        }
      });

      console.log('Meta tags establecidos correctamente');
    } catch (error) {
      console.error('Error al establecer meta tags:', error);
    }
  }

  /**
   * Elimina los meta tags existentes para evitar duplicados
   */
  private removeExistingMetaTags(): void {
    // Eliminar Open Graph tags
    this.meta.removeTag("property='og:title'");
    this.meta.removeTag("property='og:url'");
    this.meta.removeTag("property='og:image'");
    this.meta.removeTag("property='og:description'");
    this.meta.removeTag("property='og:type'");

    // Eliminar Twitter Card tags
    this.meta.removeTag("name='twitter:card'");
    this.meta.removeTag("name='twitter:title'");
    this.meta.removeTag("name='twitter:description'");
    this.meta.removeTag("name='twitter:image'");

    // Eliminar meta tags estándar
    this.meta.removeTag("name='description'");
  }

  /**
   * Resets meta tags to default values when leaving event pages
   */
  resetMetaTags(): void {
    this.removeExistingMetaTags();

    // Set default title
    this.title.setTitle('TIX - Entradas para Eventos');

    // Set default meta tags
    this.meta.updateTag({ name: 'description', content: 'Compra entradas para los mejores eventos en República Dominicana' });
    this.meta.updateTag({ property: 'og:title', content: 'TIX - Entradas para Eventos' });
    this.meta.updateTag({ property: 'og:description', content: 'Compra entradas para los mejores eventos en República Dominicana' });
    this.meta.updateTag({ property: 'og:image', content: 'https://tix-shared.s3.us-east-1.amazonaws.com/tix-default-bg.jpeg' });
    this.meta.updateTag({ property: 'og:url', content: 'https://tix.do' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
  }

  /**
   * Sets default meta tags for the application
   * This is an alias for resetMetaTags for better semantic clarity
   */
  setDefaultMetaTags(): void {
    this.resetMetaTags();
  }

  /**
   * Convierte una URL relativa en absoluta
   */
  private getAbsoluteUrl(url: string): string {
    if (!url) {
      return '';
    }

    // Si ya es una URL absoluta, devolverla tal cual
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Asegurar que el origen esté definido
    const baseOrigin = this.origin || 'https://tix.do';

    // Construir la URL completa
    if (url.startsWith('/')) {
      return `${baseOrigin}${url}`;
    } else {
      return `${baseOrigin}/${url}`;
    }
  }

  /**
   * Inserta meta tags directamente en el DOM durante SSR
   * Esta función es crucial para asegurar que los meta tags estén presentes
   * para bots y herramientas como curl/Postman
   */
  private insertMetaTagsDirectly(tags: { name?: string; property?: string; content: string }[]): void {
    if (!this.isServer || !this.document) {
      return;
    }

    try {
      console.log('Insertando meta tags directamente en el documento HTML...');

      const head = this.document.head || this.document.getElementsByTagName('head')[0];
      if (!head) {
        console.error('No se pudo encontrar el elemento head en el documento');
        return;
      }

      // Eliminar meta tags existentes de Open Graph y Twitter
      const existingMetaTags = this.document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"], meta[name="description"]');
      existingMetaTags.forEach((tag: any) => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });

      // Insertar nuevos meta tags
      tags.forEach(tag => {
        const meta = this.document.createElement('meta');

        if (tag.property) {
          meta.setAttribute('property', tag.property);
        }

        if (tag.name) {
          meta.setAttribute('name', tag.name);
        }

        meta.setAttribute('content', tag.content || '');
        head.appendChild(meta);

        console.log(`Meta tag insertado directamente: ${tag.property || tag.name} = ${tag.content}`);
      });
    } catch (error) {
      console.error('Error al insertar meta tags directamente:', error);
    }
  }
}
